import { useEffect as useEffectDI, useState as useStateDI } from 'react';
import querystring from 'querystring';
import isEmpty from 'lodash/isEmpty';
import { useRouter as useRouterDI } from '@atlassian/react-resource-router';

type DIOptions = {
	useRouter: typeof useRouterDI;
	useState: typeof useStateDI;
	useEffect: typeof useEffectDI;
};

const defaultOptions = {
	useRouter: useRouterDI,
	useState: useStateDI,
	useEffect: useEffectDI,
} as const;

/**
 * This is a hook that gets a querystring parameter from the URL and removes it.
 *
 * This parameter is stored in the component's state through `useState`.
 *
 * You shouldn't rely on this clearing after the source is removed from the URL within a single
 * session, since this isn't done.
 */
const useQueryParameter = (parameter: string, options?: DIOptions): string | null => {
	const { useRouter, useState, useEffect } = { ...defaultOptions, ...(options || {}) };

	const [{ location, query }, { replace }] = useRouter();
	const [stateValue, setValue] = useState<unknown>(null);

	const { [parameter]: value, ...otherQuery } = query;
	const targetUrl = `${location.pathname}${
		!isEmpty(otherQuery) ? `?${querystring.stringify(otherQuery)}` : ''
	}`;

	useEffect(() => {
		if (value) {
			setValue(value);
		}
	}, [setValue, value]);

	useEffect(() => {
		replace(targetUrl);
	}, [replace, targetUrl]);

	// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'string | null'.
	return value || stateValue;
};

export default useQueryParameter;
